import React, { useState } from "react";
import { Mail, Check, Bell } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Reusable "Remind me before Thursday's cutoff" opt-in form.
 * Used by the postcode-checker success state, the footer newsletter
 * block, and the exit-intent popup — all three POST to the same list.
 *
 * Props:
 *  - source          "postcode_check" | "footer" | "exit_intent"
 *  - postcode        (optional) autoheight from a postcode check
 *  - variant         "light" (default) | "dark" (footer)
 *  - label           heading text ("Never miss the cutoff")
 *  - microcopy       tiny text below the form
 *  - buttonLabel     button copy ("Sign up" | "Remind me")
 *  - testidPrefix    data-testid namespace
 *  - onSuccess       (optional) callback fired after successful submit
 */
export default function ReminderSignup({
  source,
  postcode = "",
  variant = "light",
  label = "Never miss the cutoff",
  microcopy = "One reminder before Thursday 11pm. No spam, unsubscribe anytime.",
  buttonLabel = "Sign up",
  testidPrefix = "reminder",
  onSuccess,
}) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const em = email.trim();
    if (!em || !/@/.test(em)) return;
    setLoading(true);
    try {
      await api.post("/leads/reminder-signup", {
        email: em,
        source,
        postcode: postcode || undefined,
      });
      setDone(true);
      if (onSuccess) onSuccess({ email: em });
    } catch {
      // Fail silently — this must not block the shop flow.
    } finally {
      setLoading(false);
    }
  };

  const isDark = variant === "dark";
  const inputCls = isDark
    ? "w-full px-4 py-3 text-sm bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:border-white"
    : "w-full px-4 py-3 text-sm border border-[color:var(--tbb-line)] bg-white focus:outline-none focus:border-black";
  const btnCls = isDark ? "btn-burgundy justify-center" : "btn-burgundy justify-center";
  const labelCls = isDark ? "text-white" : "text-[color:var(--tbb-ink)]";
  const microCls = isDark ? "text-white/60" : "text-[color:var(--tbb-muted)]";

  if (done) {
    return (
      <div className="flex items-start gap-2" data-testid={`${testidPrefix}-done`}>
        <Check size={16} className={`shrink-0 mt-0.5 ${isDark ? "text-white" : "text-[color:var(--tbb-burgundy)]"}`} strokeWidth={3} />
        <div>
          <div className={`text-sm font-bold ${labelCls}`}>You&apos;re set.</div>
          <div className={`text-xs ${microCls}`}>We&apos;ll email you before Thursday&apos;s 11pm cutoff.</div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`${testidPrefix}-form-wrap`}>
      {label && (
        <div className={`text-sm font-bold ${labelCls} flex items-center gap-2`}>
          <Bell size={14} className={isDark ? "text-white/80" : "text-[color:var(--tbb-burgundy)]"} />
          {label}
        </div>
      )}
      <form onSubmit={submit} className="mt-2 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
        <div className="relative">
          <Mail size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? "text-white/50" : "text-[color:var(--tbb-muted)]"}`} />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            className={`${inputCls} pl-9`}
            data-testid={`${testidPrefix}-email`}
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className={btnCls}
          data-testid={`${testidPrefix}-submit`}
        >
          {loading ? "Signing up…" : buttonLabel}
        </button>
      </form>
      {microcopy && (
        <div className={`text-[10px] mt-2 ${microCls}`} data-testid={`${testidPrefix}-microcopy`}>
          {microcopy}
        </div>
      )}
    </div>
  );
}
